import React, { useState, useRef, useEffect } from 'react';
import { Close, ArrowLeft, ArrowRight } from '@nike/nike-design-system-icons';
import PropTypes from 'prop-types';
import cx from 'clsx';
import { CLICK_MOD_EVENTS } from '@nike/ciclp-utils';
import { extractIndexFromAnalyticsId } from '../../helpers/extractIndexFromAnalyticsId';
import {
  FADE_OUT_DURATION,
  FADE_IN_DURATION,
  CONTENT_TYPES,
  ANIMATION_STATES,
} from './constants';
import { VideoCard } from '../videoCard';
import ImageCard from '../imageCard';
import styles from './animatedContent.styl';

const CARD_TYPES = { VIDEO: 'VIDEO' };

const transformSlideToFeature = slide => ({
  autoPlay: slide.autoPlay ?? false,
  contentType: slide.containerType || CONTENT_TYPES.VIDEO,
  contentUrl: slide.landscapePosterUrl || '',
  description: slide.bodyProps?.text || '',
  id: slide.id,
  subTitle: slide.subtitleProps?.text || '',
  title: slide.titleProps?.text || '',
});

const suppressPosterProps = {
  landscapePosterUrl: undefined,
  portraitPosterUrl: undefined,
};

const suppressTextProps = {
  actionButtons: [],
  bodyProps: { text: '' },
  subtitleProps: { text: '' },
  titleProps: { text: '' },
};

const renderSlideMedia = (slide, extraProps = {}) => {
  if (!slide) return null;

  const isVideo = slide.containerType?.toUpperCase() === CARD_TYPES.VIDEO;
  const mergedProps = { ...slide, ...extraProps };

  if (isVideo) {
    return (
      <VideoCard
        {...mergedProps}
        preset="ambient"
        dataAnalyticsHasLandmark={false}
      />
    );
  }
  return <ImageCard {...mergedProps} dataAnalyticsHasLandmark={false} />;
};

const slideShape = PropTypes.shape({
  autoPlay: PropTypes.bool,
  bodyProps: PropTypes.shape({ text: PropTypes.string }),
  containerType: PropTypes.string,
  id: PropTypes.string.isRequired,
  landscapePosterUrl: PropTypes.string,
  subtitleProps: PropTypes.shape({ text: PropTypes.string }),
  titleProps: PropTypes.shape({ text: PropTypes.string }),
});

const resolveAccentVariant = (name = '') => {
  if (name.includes('accent-green')) return 'accent-green';
  if (name.includes('accent-pink')) return 'accent-pink';
  return null;
};

export const AnimatedContent = ({
  slides,
  backgroundColor = '#f0f0f0',
  analyticsItemId,
  hashKey,
  internalName,
  onFeatureClick,
  onClose,
  sectionTitle,
}) => {
  const sectionTitleTrimmed =
    typeof sectionTitle === 'string' ? sectionTitle.trim() : '';
  const showSectionTitle = Boolean(sectionTitleTrimmed);

  const variant = resolveAccentVariant(internalName);
  const resolvedSlides = slides;
  const rotating360Slide = resolvedSlides?.[0] || null;
  const featureSlides = resolvedSlides ? resolvedSlides.slice(1) : [];
  const features = featureSlides.map(transformSlideToFeature);

  const [activeFeatureIndex, setActiveFeatureIndex] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [animationState, setAnimationState] = useState(ANIMATION_STATES.IDLE);
  const [animatingFeatureIndex, setAnimatingFeatureIndex] = useState(null);
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const transitionTimeoutsRef = useRef(new Set());

  const itemIndex = extractIndexFromAnalyticsId(analyticsItemId);
  const isExpanded = activeFeatureIndex !== null;

  const clearPendingTransitionTimeouts = () => {
    transitionTimeoutsRef.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    transitionTimeoutsRef.current.clear();
  };

  const scheduleTransitionTimeout = (callback, delay) => {
    const timeoutId = setTimeout(() => {
      transitionTimeoutsRef.current.delete(timeoutId);
      callback();
    }, delay);

    transitionTimeoutsRef.current.add(timeoutId);
    return timeoutId;
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer?.scrollTo) return;

    scrollContainer.scrollTo({ behavior: 'smooth', left: 0 });
  }, [activeFeatureIndex]);

  useEffect(() => {
    return () => {
      clearPendingTransitionTimeouts();
    };
  }, []);

  // Observer for analytics viewed event at 50% visibility
  useEffect(() => {
    const viewedObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          CLICK_MOD_EVENTS.trackTemporaryView('animatedContentViewed', {
            item_id: analyticsItemId
              ? String(analyticsItemId).slice(0, 100)
              : 'unknown',
          });
        }
      },
      {
        rootMargin: '0px',
        threshold: 0.5,
      },
    );

    if (sectionRef.current) {
      viewedObserver.observe(sectionRef.current);
    }

    return () => {
      if (viewedObserver) {
        viewedObserver.disconnect();
      }
    };
  }, [analyticsItemId]);

  if (!features.length) {
    return null;
  }

  const transitionToFeature = index => {
    if (isTransitioning) return;
    if (activeFeatureIndex === index) return;

    if (onFeatureClick) {
      onFeatureClick(features[index], index);
    }

    setIsTransitioning(true);

    const isFeatureAlreadyActive = activeFeatureIndex !== null;

    if (isFeatureAlreadyActive) {
      setAnimationState(ANIMATION_STATES.FADING_OUT);

      scheduleTransitionTimeout(() => {
        setActiveFeatureIndex(index);
        setAnimationState(ANIMATION_STATES.FADING_IN);
        setAnimatingFeatureIndex(index);
      }, FADE_OUT_DURATION);

      scheduleTransitionTimeout(() => {
        setAnimationState(ANIMATION_STATES.IDLE);
        setIsTransitioning(false);
      }, FADE_OUT_DURATION + FADE_IN_DURATION);
    } else {
      setActiveFeatureIndex(index);
      setAnimationState(ANIMATION_STATES.FADING_IN);
      setAnimatingFeatureIndex(index);

      scheduleTransitionTimeout(() => {
        setAnimationState(ANIMATION_STATES.IDLE);
        setIsTransitioning(false);
      }, FADE_IN_DURATION);
    }
  };

  const handleFeatureClick = index => {
    if (isTransitioning || activeFeatureIndex === index) return;

    const feature = features[index];
    CLICK_MOD_EVENTS.trackTemporaryClick('animatedFeatureClick', {
      feat_id: feature?.id ? String(feature.id).slice(0, 100) : 'unknown',
      index: String(index),
      title: feature?.title ? String(feature.title).slice(0, 100) : 'unknown',
    });

    transitionToFeature(index);
  };

  const handleClose = () => {
    if (isTransitioning || !isExpanded) return;

    CLICK_MOD_EVENTS.trackTemporaryClick('animatedCloseClick');

    setIsTransitioning(true);
    setAnimationState(ANIMATION_STATES.FADING_OUT);

    scheduleTransitionTimeout(() => {
      setActiveFeatureIndex(null);
      setAnimatingFeatureIndex(null);
      setAnimationState(ANIMATION_STATES.FADING_IN);
    }, FADE_OUT_DURATION);

    scheduleTransitionTimeout(() => {
      setAnimationState(ANIMATION_STATES.IDLE);
      setIsTransitioning(false);
      if (onClose) {
        onClose();
      }
    }, FADE_OUT_DURATION + FADE_IN_DURATION);
  };

  const renderBackgroundMedia = () => {
    const mediaClasses = styles.backgroundMedia;

    if (!isExpanded && rotating360Slide) {
      return (
        <div className={mediaClasses}>
          <div className={styles.grayBackground} />
          <div className={styles.rotatingVideoFrame}>
            {renderSlideMedia(rotating360Slide, suppressPosterProps)}
          </div>
        </div>
      );
    }

    return (
      <div className={mediaClasses}>
        <div className={styles.grayBackground} />
      </div>
    );
  };

  // Render feature buttons
  const renderFeatureButtons = () => {
    return features.map((feature, index) => {
      const isActive = activeFeatureIndex === index;
      const buttonClasses = cx(styles.featureButton, {
        [styles.featureButtonActive]: isActive,
        [styles.featureButtonInactive]: isExpanded && !isActive,
      });
      const hasDescription = Boolean(feature.description);

      const titleHeadingId = `animated-feature-title-${feature.id}`;

      const featureButton = (
        <button
          type="button"
          onClick={() => handleFeatureClick(index)}
          disabled={isTransitioning}
          aria-expanded={isActive}
          aria-label={feature.title}
          data-analytics-action-id={feature.id}
          data-click-mod={hashKey}
          data-index={index}
          className={cx(
            styles.featureButtonInner,
            hasDescription && styles.featureButtonInnerInSurface,
          )}
        >
          <span className={styles.featureButtonText}>{feature.title}</span>
          <span className={styles.featureButtonIcon} aria-hidden="true">
            +
          </span>
        </button>
      );

      return (
        <div key={feature.id} className={buttonClasses}>
          {hasDescription ? (
            <div
              className={cx(
                styles.featureShell,
                isActive && styles.featureShellExpanded,
              )}
            >
              <div
                className={cx(
                  styles.featureCollapsedRow,
                  isActive && styles.featureCollapsedRowHidden,
                )}
                aria-hidden={isActive}
              >
                {featureButton}
              </div>
              <div
                className={cx(
                  styles.featureExpandedSlot,
                  isActive && styles.featureExpandedSlotOpen,
                )}
                aria-hidden={!isActive}
              >
                <div className={styles.featureExpandedSlotInner}>
                  {isActive && (
                    <div
                      className={styles.featureSentence}
                      role="region"
                      aria-labelledby={titleHeadingId}
                    >
                      <strong
                        id={titleHeadingId}
                        className={styles.featureTitleInSentence}
                      >
                        {feature.subTitle || feature.title}
                      </strong>
                      <p className={styles.featureSentenceBody}>
                        {feature.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            featureButton
          )}
        </div>
      );
    });
  };

  return (
    <section
      ref={sectionRef}
      data-analytics-placement-id={analyticsItemId}
      data-analytics-has-landmark="true"
      data-qa="animated-content"
      data-click-mod={hashKey}
      data-index={itemIndex}
      className={cx(styles.animatedContent, {
        [styles.animatedContentAccentGreen]: variant === 'accent-green',
      })}
      aria-live="polite"
    >
      {showSectionTitle && (
        <div
          className={styles.sectionTitleBar}
          data-qa="animated-content-section-title"
        >
          <p className={styles.sectionTitleText}>{sectionTitleTrimmed}</p>
        </div>
      )}
      <div className={styles.animatedContentMain} style={{ backgroundColor }}>
        {/* Background media layer */}
        <div
          className={cx(styles.backgroundLayer, {
            [styles.fadeOut]: animationState === ANIMATION_STATES.FADING_OUT,
            [styles.fadeIn]: animationState === ANIMATION_STATES.FADING_IN,
          })}
        >
          {renderBackgroundMedia()}
        </div>

        {/* Animation Overlay for feature content */}
        {animatingFeatureIndex !== null &&
          (() => {
            const slide = featureSlides[animatingFeatureIndex];
            if (!slide) return null;

            return (
              <div
                key={slide.id}
                className={cx(styles.animationOverlay, {
                  [styles.animationOverlayFadeIn]:
                    animationState === ANIMATION_STATES.FADING_IN,
                  [styles.animationOverlayFadeOut]:
                    animationState === ANIMATION_STATES.FADING_OUT,
                })}
              >
                <div className={styles.animationFrame}>
                  {renderSlideMedia(slide, {
                    ...suppressTextProps,
                    ...suppressPosterProps,
                  })}
                </div>
              </div>
            );
          })()}

        {isExpanded && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            disabled={isTransitioning}
            aria-label="Close and return to start"
            data-analytics-action-id="animated-content-close"
            data-click-mod={hashKey}
          >
            <Close className={styles.closeButtonIcon} />
          </button>
        )}

        {isExpanded && activeFeatureIndex > 0 && (
          <button
            type="button"
            className={styles.mobileNavPrev}
            onClick={() => {
              CLICK_MOD_EVENTS.trackTemporaryClick('animatedNavClick', {
                direction: 'previous',
              });
              transitionToFeature(activeFeatureIndex - 1);
            }}
            disabled={isTransitioning}
            aria-label="Previous feature"
          >
            <ArrowLeft />
          </button>
        )}
        {isExpanded && activeFeatureIndex < features.length - 1 && (
          <button
            type="button"
            className={styles.mobileNavNext}
            onClick={() => {
              CLICK_MOD_EVENTS.trackTemporaryClick('animatedNavClick', {
                direction: 'next',
              });
              transitionToFeature(activeFeatureIndex + 1);
            }}
            disabled={isTransitioning}
            aria-label="Next feature"
          >
            <ArrowRight />
          </button>
        )}

        {/* Content layer */}
        <div className={styles.contentLayer}>
          <div
            ref={scrollContainerRef}
            className={cx(
              styles.scrollableContent,
              isExpanded && styles.scrollableContentExpanded,
            )}
          >
            <div className={styles.featureButtonsContainer}>
              {renderFeatureButtons()}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

AnimatedContent.propTypes = {
  analyticsItemId: PropTypes.string,
  backgroundColor: PropTypes.string,
  hashKey: PropTypes.string,
  internalName: PropTypes.string,
  onClose: PropTypes.func,
  onFeatureClick: PropTypes.func,
  sectionTitle: PropTypes.string,
  slides: PropTypes.arrayOf(slideShape),
};

export default AnimatedContent;
